package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.react.{GenericState, GenericStateGenerator, RegisteredChangeAttribute, StateAttribute}
import com.neurobs.ccabserver.transcriptreview.util.{EditableTranscript, EditableTranscriptWord, NonEditableTranscript, NonEditableTranscriptWord, RecordingTranscriptReviewHandler}
import com.neurobs.js.view.AudioViewer
import com.neurobs.util.TimeSeconds
import org.scalajs.dom.console
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement

import scala.concurrent.ExecutionContext.Implicits.global

case class RecordingTranscriptReviewState
(
  recordingIndex: Int,
  handler: RecordingTranscriptReviewHandler,
  viewer: AudioViewer,
  focusedWord: StateAttribute[EditableTranscriptWord],
  highlightedWord: StateAttribute[Option[EditableTranscriptWord]],
  highlightedEngine: StateAttribute[String],
  registeredChange: RegisteredChangeAttribute,
) extends GenericState {

  //def speechAudioFileFilename: String = handler.sourceData.sourceAudio.metadata.filename
  def code: String = handler.code

  def casrTranscript: NonEditableTranscript = handler.casrTranscript
  def asrTranscripts: Seq[NonEditableTranscript]  = handler.asrTranscripts
	def reviewedTranscripts: Seq[NonEditableTranscript] = handler.reviewedTranscripts
  def reviewingTranscript: EditableTranscript = handler.reviewingTranscript

  def registerChange() = registeredChange.register()

  def setFocusedWord(word: EditableTranscriptWord) = setState(focusedWord, word)

  def setHighlightedWord(word: EditableTranscriptWord) = setOptionState(highlightedWord, word)
  def clearHighlightedWord() = voidState(highlightedWord)

  def setHighlightedEngine(engineIdentifier: String) = setState(highlightedEngine, engineIdentifier)
  def clearHighlightedEngine() = setState(highlightedEngine, "")

  def calculateLeftMargin(word: EditableTranscriptWord, pixelsPerSecond: Int): Double = {
    val secondsToLeft = handler.indexOfWord(word) match {
      case None =>
        console.error(message = s"Word '${word.word}' starting at ${word.startTime} seconds was searched for but does not exist in reviewed transcript")
        word.startTime
      case Some(index) =>
        if (index == 0) word.startTime - handler.sourceData.audioSegment.interval.begin
        else {
          word.startTime - reviewingTranscript.transcriptWords(index - 1).endTime
        }
    }
    secondsToLeft.seconds() * pixelsPerSecond
  }

  def calculateLeftMargin(word: NonEditableTranscriptWord, transcript: NonEditableTranscript, pixelsPerSecond: Int): Double = {
    val secondsToLeft = {
        handler.indexOfWord(word, transcript) match {
          case None =>
            console.error(message = s"Word '${word.word}' starting at ${word.startTime} seconds was searched for but does not exist in ${transcript.engineIdentifier} transcript")
            word.startTime
          case Some(index) =>
            if (index == 0) word.startTime - handler.sourceData.audioSegment.interval.begin
            else word.startTime - transcript.nonEditableTranscriptWords(index - 1).endTime
        }
    }
    secondsToLeft.seconds() * pixelsPerSecond
  }

//  def promoteEngineTranscriptToReviewed(engineIdentifier: String) = {
//    reviewHandler.promoteEngineTranscriptToReviewed(engineIdentifier).map(_ => registerChange())
//  }
}


object RecordingTranscriptReviewStateGenerator extends GenericStateGenerator[RecordingTranscriptReviewState] {

  override def generateEmptyDefaultState() = {
    RecordingTranscriptReviewState(0, null, null, null, null, null, null)
  }
}

@react
object RecordingTranscriptReviewStateProvider {

  case class Props(handler: RecordingTranscriptReviewHandler, viewer: AudioViewer,  recordingIndex: Int, children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

    implicit val errorState = ErrorState.accessState

    val focusedWord = StateAttribute(useState(
      props.handler.reviewingTranscript.transcriptWords.headOption.getOrElse(EditableTranscriptWord())
    ))
    val highlightedWord = StateAttribute(useState[Option[EditableTranscriptWord]](None))
    val highlightedEngine = StateAttribute(useState(default = ""))
    val registeredChange = RegisteredChangeAttribute(useState(default = 0))

    val recordingTranscriptReviewState = RecordingTranscriptReviewState(
      recordingIndex = props.recordingIndex,
      handler = props.handler,
      viewer = props.viewer,
      focusedWord = focusedWord,
      highlightedWord = highlightedWord,
      highlightedEngine = highlightedEngine,
      registeredChange = registeredChange
    )

    RecordingTranscriptReviewStateGenerator.Provider(recordingTranscriptReviewState)(
      props.children:_*
    )
  }
}

